import { useState, useEffect } from 'react';
import { getClientAccountHistory, assignAccountToClient, getUsers } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { UserCog, Plus, Clock, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

export default function ClientAccountHistory({ clientId, canEdit = false }) {
  const [history, setHistory] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (clientId) {
      loadData();
    }
  }, [clientId]);

  const loadData = async () => {
    try {
      const [historyRes, accountsRes] = await Promise.all([
        getClientAccountHistory(clientId),
        getUsers('account'),
      ]);
      setHistory(historyRes.data);
      setAccountUsers(accountsRes.data);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare lo storico', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAccount = async () => {
    if (!selectedAccountId) {
      toast({ title: 'Errore', description: 'Seleziona un account manager', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await assignAccountToClient(clientId, selectedAccountId);
      toast({ title: 'Account Manager assegnato', description: 'Assegnazione completata' });
      setDialogOpen(false);
      setSelectedAccountId('');
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile assegnare', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const getAccountName = (accountId) => {
    const user = accountUsers.find(u => u.id === accountId);
    return user ? user.name : 'Sconosciuto';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Presente';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: it });
    } catch {
      return dateStr;
    }
  };

  // Current active account
  const currentAccount = history.find(h => !h.end_date);
  const pastAccounts = history.filter(h => h.end_date);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold">Account Manager</h3>
        </div>
        {canEdit && (
          <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            {currentAccount ? 'Cambia' : 'Assegna'}
          </Button>
        )}
      </div>

      {/* Current Account */}
      {currentAccount ? (
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="font-medium text-purple-700">
                  {getAccountName(currentAccount.account_user_id).charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium">{getAccountName(currentAccount.account_user_id)}</p>
                <p className="text-sm text-gray-500">
                  Dal {formatDate(currentAccount.start_date)}
                </p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800">Attivo</Badge>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 py-2">Nessun Account Manager assegnato</p>
      )}

      {/* History - Collapsible */}
      {pastAccounts.length > 0 && (
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen} className="mt-4 pt-4 border-t">
          <CollapsibleTrigger asChild>
            <button 
              type="button" 
              className="flex items-center gap-2 w-full text-left hover:bg-gray-50 rounded p-1 -ml-1"
            >
              {historyOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
              <Clock className="w-4 h-4 text-gray-400" />
              <h4 className="text-sm font-medium text-gray-500">
                Storico ({pastAccounts.length})
              </h4>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="space-y-2">
              {pastAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm">{getAccountName(account.account_user_id)}</span>
                  <span className="text-xs text-gray-500">
                    {formatDate(account.start_date)} - {formatDate(account.end_date)}
                  </span>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Assign Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentAccount ? 'Cambia Account Manager' : 'Assegna Account Manager'}
            </DialogTitle>
            <DialogDescription>
              {currentAccount 
                ? 'Seleziona il nuovo Account Manager. L\'assegnazione precedente verrà chiusa automaticamente.'
                : 'Seleziona l\'Account Manager da assegnare a questo cliente.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Account Manager</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona account manager" />
                </SelectTrigger>
                <SelectContent>
                  {accountUsers.length === 0 ? (
                    <SelectItem value="none" disabled>
                      Nessun account manager disponibile
                    </SelectItem>
                  ) : (
                    accountUsers.map((user) => (
                      <SelectItem 
                        key={user.id} 
                        value={user.id}
                        disabled={currentAccount?.account_user_id === user.id}
                      >
                        {user.name} ({user.email})
                        {currentAccount?.account_user_id === user.id && ' (attuale)'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="button" onClick={handleAssignAccount} disabled={saving || !selectedAccountId}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
